import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import {
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  HEADING,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  LINK,
  ORDERED_LIST,
  QUOTE,
  UNORDERED_LIST,
  $convertFromMarkdownString,
  $convertToMarkdownString,
} from '@lexical/markdown';
import { createEditor, type EditorState } from 'lexical';
import { serializeRichTextContent } from './notesProjection';

export const NOTES_MARKDOWN_TRANSFORMERS = [
  HEADING,
  QUOTE,
  UNORDERED_LIST,
  ORDERED_LIST,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  LINK,
];

const NOTES_NODES = [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode];

const EMPTY_EDITOR_STATE = {
  root: {
    children: [
      {
        children: [],
        direction: null,
        format: '',
        indent: 0,
        type: 'paragraph',
        version: 1,
      },
    ],
    direction: null,
    format: '',
    indent: 0,
    type: 'root',
    version: 1,
  },
};

type SerializedNode = {
  type?: string;
  text?: string;
  fileName?: string;
  children?: SerializedNode[];
  root?: SerializedNode;
  [key: string]: unknown;
};

function createNotesEditor() {
  return createEditor({
    namespace: 'notes-markdown-conversion',
    nodes: NOTES_NODES,
    onError: () => undefined,
  });
}

function assetPlaceholder(node: SerializedNode): SerializedNode {
  const label = typeof node.fileName === 'string' && node.fileName.trim()
    ? node.fileName.trim()
    : node.type === 'asset-image' ? 'Image' : 'Attachment';
  return {
    detail: 0,
    format: 0,
    mode: 'normal',
    style: '',
    text: `[${label}]`,
    type: 'text',
    version: 1,
  };
}

function sanitizeSerializedNode(node: SerializedNode, parentType?: string): SerializedNode {
  if (node.type === 'asset-image' || node.type === 'asset-attachment') {
    const placeholder = assetPlaceholder(node);
    if (parentType === 'root') {
      return {
        children: [placeholder],
        direction: null,
        format: '',
        indent: 0,
        type: 'paragraph',
        version: 1,
      };
    }
    return placeholder;
  }

  if (Array.isArray(node.children)) {
    return {
      ...node,
      children: node.children.map((child) => sanitizeSerializedNode(child, node.type)),
    };
  }

  return node;
}

function parseSerializedEditorState(value: unknown, sanitizeAssets = false): string | undefined {
  const serialized = serializeRichTextContent(value);
  if (!serialized) return undefined;

  try {
    const parsed = JSON.parse(serialized) as SerializedNode;
    if (!parsed || typeof parsed !== 'object' || !parsed.root) return undefined;
    if (!sanitizeAssets) return serialized;
    return JSON.stringify({ ...parsed, root: sanitizeSerializedNode(parsed.root, 'document') });
  } catch {
    return undefined;
  }
}

function parseEditorState(
  editor: ReturnType<typeof createNotesEditor>,
  value: unknown,
  sanitizeAssets = false,
): EditorState {
  const serialized = parseSerializedEditorState(value, sanitizeAssets);
  if (serialized) {
    return editor.parseEditorState(serialized);
  }

  const markdown = typeof value === 'string' ? value : '';
  editor.update(() => {
    $convertFromMarkdownString(markdown, NOTES_MARKDOWN_TRANSFORMERS);
  }, { discrete: true });
  return editor.getEditorState();
}

/** Export a durable Lexical value as canonical Markdown for source fields. */
export function lexicalNotesToMarkdown(value: unknown): string {
  if (value == null) return '';

  const editor = createNotesEditor();
  try {
    const state = parseEditorState(editor, value, true);
    return state.read(() => $convertToMarkdownString(NOTES_MARKDOWN_TRANSFORMERS));
  } catch {
    return typeof value === 'string' ? value : '';
  }
}

/** Normalize either legacy JSON, Markdown, or plain text to Lexical JSON. */
export function normalizeNotesToLexicalJSON(value: unknown): string {
  if (value == null || (typeof value === 'string' && value.trim() === '')) {
    return JSON.stringify(EMPTY_EDITOR_STATE);
  }

  const serialized = parseSerializedEditorState(value);
  if (serialized) return serialized;

  const editor = createNotesEditor();
  try {
    const state = parseEditorState(editor, value);
    return JSON.stringify(state.toJSON());
  } catch {
    return JSON.stringify(EMPTY_EDITOR_STATE);
  }
}
